import { useEffect, type JSX } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "@/components/ui/menubar"
import {
  getWordsAsync,
  resetHome,
  setDifficulty,
  toggleFilterUnderscores,
  setForeignLanguage,
  setNativeLanguage,
  setTimeMode,
  toggleMixedMode,
  toggleStrictMode,
} from "./homeSlice"
import { languageList, languages, nonAlphabetLanguages } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Blend, Lollipop, Settings, MoveHorizontal } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { toast } from "sonner"

export const Dashboard = (): JSX.Element | null => {
  const homeSlice = useAppSelector(state => state.home)
  const dispatch = useAppDispatch()

  const activeButtonColor = "bg-gray-200"

  async function fetchWords() {
    await dispatch(getWordsAsync()).unwrap()
  }

  useEffect(() => {
    void fetchWords()
  }, [dispatch, homeSlice.foreignLanguage, homeSlice.nativeLanguage, homeSlice.difficulty])

  function buttonOutline(time: number): string {
    return homeSlice.timeMode === time ? activeButtonColor : ""
  }

  function buttonOutlineDifficulty(difficulty: "beginner" | "advanced" | "native"): string {
    return homeSlice.difficulty === difficulty ? activeButtonColor : ""
  }

  function changeDifficulty(difficulty: "beginner" | "advanced" | "native"): void {
    dispatch(setDifficulty(difficulty))
    dispatch(resetHome())
  }

  function swapLanguages() {
    const temp = homeSlice.nativeLanguage
    dispatch(setNativeLanguage(homeSlice.foreignLanguage))
    dispatch(setForeignLanguage(temp))
    dispatch(resetHome())
  }

  return (
    <Menubar className="p-5">
      <TooltipProvider>
        <MenubarMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <MenubarTrigger className="cursor-pointer">
                {/* TODO: suboptimal doing it through find */}
                {/* native {languages.find(language => language.code === homeSlice.nativeLanguage)?.emoji} */}
                native {languages[homeSlice.nativeLanguage].emoji}
              </MenubarTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>your first language</p>
            </TooltipContent>
          </Tooltip>
          <MenubarContent>
            {languageList
              .filter(language => language !== homeSlice.nativeLanguage)
              .map(language => (
                <MenubarItem
                  className="cursor-pointer"
                  key={language}
                  onClick={() => {
                    dispatch(setNativeLanguage(language))
                    dispatch(resetHome())
                  }}
                >
                  {languages[language].name} <MenubarShortcut>{languages[language].emoji}</MenubarShortcut>
                </MenubarItem>
              ))}
          </MenubarContent>
        </MenubarMenu>
        <Button variant="ghost" size="icon" className="cursor-pointer" onClick={swapLanguages}>
          <MoveHorizontal className="cursor-pointer" size="1em" />
        </Button>
        <MenubarMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <MenubarTrigger className="cursor-pointer">
                target {languages[homeSlice.foreignLanguage].emoji}
              </MenubarTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>the language you would like to learn</p>
            </TooltipContent>
          </Tooltip>
          <MenubarContent>
            {languageList
              .filter(language => language !== homeSlice.foreignLanguage)
              .filter(language => language !== "ar") // TODO: Arabic is not supported yet
              .map(language => (
                <MenubarItem
                  className="cursor-pointer"
                  key={language}
                  onClick={() => {
                    dispatch(setForeignLanguage(language))
                    dispatch(resetHome())
                  }}
                >
                  {languages[language].name} <MenubarShortcut>{languages[language].emoji}</MenubarShortcut>
                </MenubarItem>
              ))}
          </MenubarContent>
        </MenubarMenu>
        <MenubarMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={homeSlice.mixedMode ? activeButtonColor : ""}
                variant="ghost"
                size="icon"
                onClick={() => {
                  dispatch(toggleMixedMode())
                  dispatch(resetHome())
                }}
              >
                <Blend />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>alternate between your native and target language</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={!homeSlice.strictMode ? activeButtonColor : ""}
                variant="ghost"
                size="icon"
                onClick={() => {
                  dispatch(toggleStrictMode())
                  if (nonAlphabetLanguages.includes(homeSlice.foreignLanguage)) dispatch(resetHome()) // reset if romanized/foreign swap
                }}
              >
                <Lollipop />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>all accents are ignored, no special keyboard layouts required</p>
            </TooltipContent>
          </Tooltip>
        </MenubarMenu>
        <MenubarMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <MenubarTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Settings />
                </Button>
              </MenubarTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>more settings</p>
            </TooltipContent>
          </Tooltip>
          <MenubarContent>
            <MenubarSub>
              <MenubarSubTrigger>difficulty</MenubarSubTrigger>
              <MenubarSubContent>
                <MenubarItem
                  className={buttonOutlineDifficulty("beginner")}
                  onClick={() => changeDifficulty("beginner")}
                >
                  beginner
                </MenubarItem>
                <MenubarItem
                  className={buttonOutlineDifficulty("advanced")}
                  onClick={() => changeDifficulty("advanced")}
                >
                  advanced
                </MenubarItem>
                <MenubarItem className={buttonOutlineDifficulty("native")} onClick={() => changeDifficulty("native")}>
                  native
                </MenubarItem>
              </MenubarSubContent>
            </MenubarSub>
            <MenubarItem
              onClick={() => dispatch(toggleFilterUnderscores())}
              className={homeSlice.filterUnderscores ? activeButtonColor : ""}
            >
              {/* <Button
                className={!homeSlice.filterUnderscores ? activeButtonColor : ""}
                variant="ghost"
                size="icon"
                onClick={() => {
                  dispatch(toggleFilterUnderscores())
                }}
              > */}
              filter out words with underscores
              {/* </Button> */}
            </MenubarItem>
            <MenubarItem onClick={() => toast.info("feature coming soon™")}>touch typing helper</MenubarItem>
            <MenubarItem onClick={() => toast.info("feature coming soon™")}>toggle romanized words</MenubarItem>
          </MenubarContent>
        </MenubarMenu>
        <MenubarMenu>
          <Button
            className={buttonOutline(15)}
            variant="ghost"
            size="icon"
            onClick={() => {
              dispatch(setTimeMode(15))
              dispatch(resetHome())
            }}
          >
            15
          </Button>
          <Button
            className={buttonOutline(30)}
            variant="ghost"
            size="icon"
            onClick={() => {
              dispatch(setTimeMode(30))
              dispatch(resetHome())
            }}
          >
            30
          </Button>
          <Button
            className={buttonOutline(60)}
            variant="ghost"
            size="icon"
            onClick={() => {
              dispatch(setTimeMode(60))
              dispatch(resetHome())
            }}
          >
            60
          </Button>
        </MenubarMenu>
      </TooltipProvider>
    </Menubar>
  )
}
